import { QrCode, Wordmark, cn } from '@westhetics/ui'
import type { RegistrationLink } from './api'
import { PIECE_STEPS, type PieceFormat } from './piece-formats'

/**
 * A peça impressa do link de cadastro (docs/areas/pacientes.md, "Link de cadastro"; T37): o
 * wordmark da Westhetics, o logo da clínica (ou o nome em Lexend), o convite, o QR grande, o
 * endereço por extenso, os três passos e o fecho. Uma peça só, em qualquer formato: todo tamanho é
 * relativo à largura do contêiner (`cqw`), então a prévia na tela e a folha na impressora são a mesma
 * coisa em escalas diferentes. Só tokens; não é componente do design system (é uma tela do web).
 */

export function RegistrationPiece({
  link,
  format,
  className,
}: {
  link: RegistrationLink
  format: PieceFormat
  className?: string
}) {
  const { clinic } = link
  const place = [clinic.city, clinic.state].filter(Boolean).join(' · ')
  const stacked = format === 'a6'
  return (
    <div
      data-slot="registration-piece"
      data-format={format}
      className={cn(
        '@container w-full [-webkit-print-color-adjust:exact] [print-color-adjust:exact]',
        className,
      )}
    >
      <div className="bg-pampas text-ink flex aspect-[1/1.414] w-full flex-col p-[7cqw]">
        <div className="flex items-start justify-between gap-[4cqw]">
          <Wordmark className="h-[2.6cqw] w-auto shrink-0" />
          {clinic.logoUrl ? (
            <img
              src={clinic.logoUrl}
              alt={clinic.name}
              className="h-[9cqw] w-auto max-w-[36cqw] object-contain object-right"
            />
          ) : (
            <span className="font-display max-w-[52cqw] text-right text-[3cqw] leading-[1.2] font-light">
              {clinic.name}
            </span>
          )}
        </div>

        <div className="mt-[9cqw]">
          <div className="font-body text-label text-[2.2cqw] leading-none font-normal tracking-[.14em] uppercase">
            Cadastro de paciente
          </div>
          <h2 className="font-display mt-[2.4cqw] text-[7.2cqw] leading-[1.08] font-light text-pretty">
            Faça seu cadastro antes de chegar.
          </h2>
          <p className="font-body text-secondary mt-[2.4cqw] text-[3cqw] leading-[1.5] font-light">
            {clinic.name}
            {place ? ` · ${place}` : ''}
          </p>
        </div>

        <div className="my-auto flex flex-col items-center gap-[3cqw] py-[4cqw]">
          <QrCode
            value={link.url}
            label={`QR do link de cadastro da ${clinic.name}`}
            size={480}
            className="h-auto w-[46cqw] border-0"
          />
          <p className="font-body text-secondary max-w-[76cqw] text-center text-[2.3cqw] leading-[1.5] font-light break-all">
            {link.url}
          </p>
        </div>

        <ol
          className={cn(
            'border-hairline-heavy border-t pt-[3.6cqw]',
            stacked ? 'flex flex-col gap-[2.2cqw]' : 'grid grid-cols-3 gap-[3cqw]',
          )}
        >
          {PIECE_STEPS.map((step, index) => (
            <li key={step} className="flex items-baseline gap-[1.6cqw]">
              <span className="font-display text-label text-[2.6cqw] leading-none font-light tabular-nums">
                {index + 1}
              </span>
              <span className="font-display text-[3cqw] leading-[1.25] font-light">{step}</span>
            </li>
          ))}
        </ol>

        <div className="mt-[6cqw] flex items-end justify-between gap-[4cqw]">
          <p className="font-display text-[3cqw] leading-none font-light">
            A beleza deve ser segura.
          </p>
          <p className="font-body text-label text-[2.2cqw] leading-none font-light">
            westhetics.io
          </p>
        </div>
      </div>
    </div>
  )
}
